import { useCallback, useId, useMemo, useState } from "react"

import { ENTITY_ICONS } from "@/constants/entity-icons"
import { useMetricsList } from "@/metrics/catalog"
import { getMetricFilterConfig } from "@/metrics/metric-display"
import { useMetricDirectory } from "@/metrics/registry"
import { Button, GroupLabel, Input, Label, SelectDropdown } from "@coval/ui"
import { Plus, X } from "lucide-react"

import {
  formatMetricValueFilterLabel,
  getMetricValueFilterKey,
  NUMERIC_METRIC_VALUE_FILTER_OPERATORS,
  STRING_METRIC_VALUE_FILTER_OPERATORS,
  type MetricValueFilterOperator,
} from "@/lib/metricValueFilters"
import { FilterPopover } from "@/components/filters/FilterPopover"
import { isFilterSectionDisabled } from "@/components/filters/filterSectionVisibility"
import { FilterTrigger } from "@/components/filters/FilterTrigger"
import { MetricFilterModeToggle } from "@/components/filters/MetricFilterModeToggle"
import { MetricValuePicker } from "@/components/filters/MetricValuePicker"
import { SearchableCheckboxList } from "@/components/filters/SearchableCheckboxList"

import { useRunsFilter } from "../../contexts/RunsFilterContext"
import { useRunsMetricStringValuesTransport } from "../../routes/useMetricStringValuesTransport"
import type { FilterSectionProps } from "../../types/filters"
import {
  buildMetricIdsFilter,
  findMetricRecordForFilterValue,
} from "../../utils/runsTableState"

type MetricsPanelMode = "presence" | "value"
const MetricsIcon = ENTITY_ICONS.metrics

export const MetricsFilterSection: React.FC<FilterSectionProps> = ({
  isOpen,
  onToggle,
  filterEnabled,
}) => {
  const {
    metricFilters,
    metricValueFilters,
    setMetricFilters,
    addMetricValueFilter,
    removeMetricValueFilter,
  } = useRunsFilter()
  const {
    data: metricsData,
    isError,
    isLoading,
    metricIdAliases,
  } = useMetricsList({
    enabled: filterEnabled,
    referencedMetricIds: metricFilters,
  })

  const { byManager, isLoading: directoryLoading } = useMetricDirectory()

  const [selectedMetricId, setSelectedMetricId] = useState("")
  const [selectedOperator, setSelectedOperator] =
    useState<MetricValueFilterOperator>("=")
  const [filterValue, setFilterValue] = useState("")
  const [rangeMaxValue, setRangeMaxValue] = useState("")
  const [panelMode, setPanelMode] = useState<MetricsPanelMode>("presence")

  const operatorLabelId = useId()
  const valueInputId = useId()
  const minInputId = useId()
  const maxInputId = useId()

  const metrics = useMemo(() => {
    if (!metricsData) {
      return []
    }

    return Object.values(metricsData).sort((left, right) =>
      (left.metric_display_name || left.metric_name).localeCompare(
        right.metric_display_name || right.metric_name,
      ),
    )
  }, [metricsData])

  const checkboxItems = useMemo(
    () =>
      metrics.map((metric) => ({
        id: metric.id,
        label: metric.metric_display_name || metric.metric_name,
        searchValue: `${metric.metric_display_name || ""} ${metric.metric_name}`,
      })),
    [metrics],
  )

  // For simulated conversations, shared metric value filters store the metric
  // record id in `metricName` so we can round-trip URL state and query by id.
  const metricsById = useMemo(
    () => Object.fromEntries(metrics.map((metric) => [metric.id, metric])),
    [metrics],
  )
  const selectedMetricPresenceIds = useMemo(
    () => buildMetricIdsFilter(metricFilters, metricsById, metricIdAliases),
    [metricFilters, metricsById, metricIdAliases],
  )

  const selectedMetric = selectedMetricId
    ? metricsById[selectedMetricId]
    : undefined
  const selectedConfig = selectedMetric
    ? getMetricFilterConfig(
        selectedMetric,
        byManager[selectedMetric.metric_manager],
      )
    : undefined
  const selectedMetricType =
    selectedConfig?.outputType === "float"
      ? "number"
      : selectedConfig?.outputType === "string"
        ? "string"
        : "unsupported"
  const declaredOptions = selectedConfig?.valueOptions

  const {
    data: stringValueOptions,
    isLoading: isLoadingStringValues,
    isError: stringValuesError,
    refetch: retryStringValues,
  } = useRunsMetricStringValuesTransport(selectedMetricId, {
    enabled:
      selectedMetricType === "string" &&
      !declaredOptions &&
      filterEnabled !== false &&
      !!selectedMetricId,
    isMonitoring: false,
  })

  const operatorOptions = declaredOptions
    ? (["=", "!="] as const)
    : selectedMetricType === "number"
      ? NUMERIC_METRIC_VALUE_FILTER_OPERATORS
      : STRING_METRIC_VALUE_FILTER_OPERATORS

  const resetMetricValueForm = useCallback(() => {
    setSelectedMetricId("")
    setSelectedOperator("=")
    setFilterValue("")
    setRangeMaxValue("")
  }, [])

  const canAddMetricValueFilter = useMemo(() => {
    if (
      !selectedMetricId ||
      directoryLoading ||
      selectedMetricType === "unsupported"
    ) {
      return false
    }

    if (selectedOperator === "range") {
      const minValue = Number.parseFloat(filterValue)
      const maxValue = Number.parseFloat(rangeMaxValue)
      return (
        Number.isFinite(minValue) &&
        Number.isFinite(maxValue) &&
        minValue <= maxValue
      )
    }

    if (selectedMetricType === "number") {
      return Number.isFinite(Number.parseFloat(filterValue))
    }

    return filterValue.trim().length > 0
  }, [
    directoryLoading,
    filterValue,
    rangeMaxValue,
    selectedMetricId,
    selectedMetricType,
    selectedOperator,
  ])

  const handleMetricChange = useCallback((metricId: string) => {
    setSelectedMetricId(metricId)
    setSelectedOperator("=")
    setFilterValue("")
    setRangeMaxValue("")
  }, [])

  const handleAddMetricValueFilter = useCallback(() => {
    if (!selectedMetricId || !canAddMetricValueFilter) {
      return
    }

    if (selectedMetricType === "number") {
      const numericValue = Number.parseFloat(filterValue)

      addMetricValueFilter({
        metricName: selectedMetricId,
        operator: selectedOperator,
        value: numericValue,
        rangeMax:
          selectedOperator === "range"
            ? Number.parseFloat(rangeMaxValue)
            : undefined,
        valueType: "number",
      })
    } else if (selectedMetricType === "string") {
      addMetricValueFilter({
        metricName: selectedMetricId,
        operator: selectedOperator,
        value: filterValue,
        valueType: "string",
      })
    }

    resetMetricValueForm()
  }, [
    addMetricValueFilter,
    canAddMetricValueFilter,
    filterValue,
    rangeMaxValue,
    resetMetricValueForm,
    selectedMetricId,
    selectedMetricType,
    selectedOperator,
  ])

  const isDisabled = isFilterSectionDisabled({
    activeFilterCount: metricFilters.length + metricValueFilters.length,
    isDeferred: filterEnabled === false,
    isError,
    isLoading,
    optionCount: metrics.length,
  })

  return (
    <FilterPopover
      isOpen={isOpen}
      onToggle={onToggle}
      disabled={isDisabled}
      emptyMessage="No metrics available in this workspace."
      trigger={
        <FilterTrigger
          label="Metrics"
          icon={<MetricsIcon className="w-4 h-4" />}
          isActive={metricFilters.length > 0 || metricValueFilters.length > 0}
          disabled={isDisabled}
        />
      }
    >
      <MetricFilterModeToggle
        mode={panelMode}
        onModeChange={setPanelMode}
        presenceCount={metricFilters.length}
        valueCount={metricValueFilters.length}
      />

      {panelMode === "presence" ? (
        <SearchableCheckboxList
          items={checkboxItems}
          selectedIds={selectedMetricPresenceIds}
          onSelectionChange={setMetricFilters}
          isLoading={isLoading}
          isDeferred={filterEnabled === false}
          isOpen={isOpen}
          placeholder="Search metrics..."
          emptyMessage="No metrics found"
        />
      ) : (
        <div className="min-w-0 w-full p-3 space-y-3">
          {!selectedMetricId && metricValueFilters.length > 0 && (
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {metricValueFilters.map((filter) => {
                const metric =
                  metricsById[filter.metricName] ||
                  findMetricRecordForFilterValue(filter.metricName, metrics)
                const metricLabel =
                  metric?.metric_display_name ||
                  metric?.metric_name ||
                  filter.metricName

                return (
                  <div
                    key={getMetricValueFilterKey(filter)}
                    className="flex items-center justify-between gap-2 rounded-md bg-surface-1 px-2 py-1.5"
                  >
                    <span className="truncate text-xs font-medium">
                      {formatMetricValueFilterLabel(filter, metricLabel)}
                    </span>
                    <Button
                      icon
                      variant="ghost"
                      onClick={() => removeMetricValueFilter(filter)}
                      tooltip={`Remove filter ${formatMetricValueFilterLabel(filter, metricLabel)}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )
              })}
            </div>
          )}

          <MetricValuePicker
            items={checkboxItems}
            selectedId={selectedMetricId}
            onSelect={handleMetricChange}
            loading={isLoading || directoryLoading || filterEnabled === false}
          >
            {selectedMetricId && selectedMetricType === "unsupported" && (
              <p className="text-xs text-coval-text-muted">
                This metric type does not support value filtering yet.
              </p>
            )}

            {selectedMetricId && selectedMetricType !== "unsupported" && (
              <>
                <div className="space-y-1.5">
                  <GroupLabel id={operatorLabelId}>Condition</GroupLabel>
                  <SelectDropdown
                    aria-labelledby={operatorLabelId}
                    options={operatorOptions.map((operator) => ({
                      value: operator,
                      label: operator === "range" ? "between" : operator,
                    }))}
                    value={selectedOperator}
                    onValueChange={(value) =>
                      setSelectedOperator(value as MetricValueFilterOperator)
                    }
                  />
                </div>

                {declaredOptions ? (
                  <div className="space-y-1.5">
                    <Label htmlFor={valueInputId}>Value</Label>
                    <SelectDropdown
                      aria-label="Metric value"
                      options={declaredOptions}
                      value={filterValue}
                      onValueChange={setFilterValue}
                      placeholder="Select a value"
                    />
                  </div>
                ) : selectedMetricType === "string" ? (
                  <div className="space-y-2">
                    <Label htmlFor={valueInputId}>Value</Label>
                    <Input
                      id={valueInputId}
                      aria-label="Metric value"
                      value={filterValue}
                      onChange={(event) => setFilterValue(event.target.value)}
                      placeholder="Enter an exact value"
                    />
                    {!!stringValueOptions?.length && (
                      <SelectDropdown
                        aria-label="Recorded metric values"
                        searchable
                        options={stringValueOptions
                          .filter(Boolean)
                          .map((value) => ({ value, label: value }))}
                        value={filterValue}
                        onValueChange={setFilterValue}
                        placeholder="Choose a recorded value"
                      />
                    )}
                    {stringValuesError ? (
                      <p
                        role="alert"
                        className="text-xs text-coval-danger-text"
                      >
                        Could not load recorded values. Enter a value or{" "}
                        <Button
                          variant="link"
                          onClick={() => void retryStringValues()}
                        >
                          Retry values
                        </Button>
                        .
                      </p>
                    ) : isLoadingStringValues ? (
                      <p
                        role="status"
                        className="text-xs text-coval-text-muted"
                      >
                        Loading recorded values…
                      </p>
                    ) : (
                      !stringValueOptions?.length && (
                        <p className="text-xs text-coval-text-muted">
                          No recorded values. Enter an exact value to filter.
                        </p>
                      )
                    )}
                  </div>
                ) : selectedOperator === "range" ? (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1.5">
                      <Label htmlFor={minInputId}>Min</Label>
                      <Input
                        id={minInputId}
                        value={filterValue}
                        onChange={(event) => setFilterValue(event.target.value)}
                        type="number"
                        aria-label="Minimum metric value"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor={maxInputId}>Max</Label>
                      <Input
                        id={maxInputId}
                        value={rangeMaxValue}
                        onChange={(event) =>
                          setRangeMaxValue(event.target.value)
                        }
                        type="number"
                        aria-label="Maximum metric value"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label htmlFor={valueInputId}>Value</Label>
                    <Input
                      id={valueInputId}
                      value={filterValue}
                      onChange={(event) => setFilterValue(event.target.value)}
                      type="number"
                      aria-label="Metric value"
                    />
                  </div>
                )}

                <Button
                  onClick={handleAddMetricValueFilter}
                  disabled={!canAddMetricValueFilter}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add value filter
                </Button>
              </>
            )}
          </MetricValuePicker>
        </div>
      )}
    </FilterPopover>
  )
}
